import './TimelineBarChart.scss';

import * as Dfns from 'date-fns/fp';
import cn from 'classnames';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  XAxisTickContentProps,
  YAxis,
  YAxisTickContentProps
} from 'recharts';
import { TimelineEntry } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Brand50, Neutral200, Neutral300 } from '../../../utils/colors';
import { DateRange } from '../../../utils/date-utils';
import {
  identifyTimelineEntries,
  OrderByOptions,
  isValidTimelineEntry,
  sortTimelineEntriesAlphabetically,
  sortTimelineEntriesByDateAsc,
  truncateTimelineEntryDates
} from './OfflineInputsUtils';
import { TimelineAxisRow } from './TimelineAxisRow';

interface TimelineBarChartProps {
  timelineEntries: TimelineEntry[];
  timelineDateRange: DateRange;
  orderBy: OrderByOptions;
  setNumVisibleEntries: React.Dispatch<React.SetStateAction<number>>;
  editingUnlocked?: boolean;
  pushStagedDelete?: (newDelete: TimelineEntry) => void;
}

// how much of each row (bar) does the purple cell take up
const CELL_TO_BAR_RATIO = 0.4;

const CHART_LEFT_MARGIN = 16;
const X_AXIS_TICK_COMPONENT_HEIGHT = 24;
const X_AXIS_TICK_HORIZONTAL_PADDING = 4;
const X_AXIS_TICK_VERTICAL_OFFSET = 2;
const Y_AXIS_TICK_WIDTH = 496;

export const TimelineBarChart = ({
  timelineEntries,
  timelineDateRange,
  orderBy,
  setNumVisibleEntries,
  editingUnlocked,
  pushStagedDelete
}: TimelineBarChartProps) => {
  const dateMin = Dfns.parseISO(timelineDateRange.start);
  const dateMax = Dfns.parseISO(timelineDateRange.end);
  const xAxisTickInterval = Dfns.eachMonthOfInterval({
    start: Dfns.startOfMonth(dateMin),
    end: Dfns.add({ months: 1 })(Dfns.startOfMonth(dateMax))
  }).map(date => date.getTime());
  const timeMin = dateMin.getTime();
  const timeMax = dateMax.getTime();
  const [chartWidth, setChartWidth] = useState<number>(0);
  const [hoveredValue, setHoveredValue] = useState<string | number | undefined>();
  const [barHeight, setBarHeight] = useState<number>(0);
  // `renderBarBackground` runs during recharts' render, so it latches the measured bar height in a
  // ref and the effect flushes it to state after commit — avoids a setState during render.
  const measuredBarHeightRef = useRef(0);
  // Flush on every commit (no deps): the ref is written during recharts' render on each pass
  // (incl. resize), and the `!== barHeight` guard makes the update idempotent, so this cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (measuredBarHeightRef.current && measuredBarHeightRef.current !== barHeight) {
      setBarHeight(measuredBarHeightRef.current);
    }
  });

  // Every row needs its own ID: see identifyTimelineEntries.
  const identifiedEntries = useMemo(
    () => identifyTimelineEntries(timelineEntries),
    [timelineEntries]
  );

  const processedChartData = useMemo(() => {
    const filteredEntries = identifiedEntries
      .filter(entry => isValidTimelineEntry(entry, dateMin, dateMax))
      .map(entry => truncateTimelineEntryDates(entry, dateMin, dateMax));
    const sortedEntries =
      orderBy === OrderByOptions.ALPHABETICAL
        ? sortTimelineEntriesAlphabetically(filteredEntries)
        : sortTimelineEntriesByDateAsc(filteredEntries);
    return sortedEntries.map(
      ({ promoPeriodId, promoName, specialOfferName, percentageDiscount, startDate, endDate }) => {
        const start = Dfns.parseISO(startDate).getTime();
        const end = Dfns.add({ days: 1 })(Dfns.parseISO(endDate)).getTime();
        return {
          promoPeriodId,
          promoName,
          specialOfferName,
          percentageDiscount,
          startDate,
          endDate,
          timeRange: [start, end]
        };
      }
    );
  }, [identifiedEntries, orderBy, dateMin, dateMax]);

  useEffect(() => {
    setNumVisibleEntries(processedChartData.length);
  }, [processedChartData.length, setNumVisibleEntries]);

  // and similarly, some slight hackery for x axis ticks
  const xAxisWidth = useMemo<number>(
    () => chartWidth - Y_AXIS_TICK_WIDTH - CHART_LEFT_MARGIN,
    [chartWidth]
  );

  const xAxisTickWidthByDay = useMemo<number>(
    () =>
      xAxisWidth /
      Dfns.differenceInDays(xAxisTickInterval[0])(xAxisTickInterval[xAxisTickInterval.length - 1]),
    [xAxisTickInterval, xAxisWidth]
  );

  // rechart tick mark type
  // note to adjust y, height values if you create new ticks
  const yAxisTick = ({ x, y, payload }: YAxisTickContentProps): ReactElement => {
    const { value: payloadId, index } = payload;
    const entryIndex = identifiedEntries.findIndex(entry => entry.promoPeriodId === payloadId);
    if (entryIndex === -1) {
      return <g />;
    }
    // Deletes get the entry as fetched, not the display ID identifyTimelineEntries gave it.
    const originalEntry = timelineEntries[entryIndex];
    return (
      <foreignObject
        x={Number(x) - Y_AXIS_TICK_WIDTH}
        y={Number(y) - barHeight / 2}
        width={Y_AXIS_TICK_WIDTH}
        height={barHeight}
        textAnchor='start'
      >
        <TimelineAxisRow
          index={index}
          timelineEntry={identifiedEntries[entryIndex]}
          editingUnlocked={editingUnlocked}
          pushStagedDelete={pushStagedDelete && (() => pushStagedDelete(originalEntry))}
        />
      </foreignObject>
    );
  };

  const xAxisTick = ({ x, y, payload }: XAxisTickContentProps): ReactElement => {
    const { value: tickDate } = payload;
    const tickIntervalWidth = Dfns.getDaysInMonth(tickDate) * xAxisTickWidthByDay;
    return (
      <foreignObject
        x={Number(x) + X_AXIS_TICK_HORIZONTAL_PADDING}
        y={(Number(y) - X_AXIS_TICK_COMPONENT_HEIGHT) / 2}
        width={tickIntervalWidth - X_AXIS_TICK_HORIZONTAL_PADDING * 2}
        // bumping by 2 to display border
        height={X_AXIS_TICK_COMPONENT_HEIGHT + X_AXIS_TICK_VERTICAL_OFFSET}
      >
        <div className='x-axis-tick'>{Dfns.format("MMM ''yy")(tickDate)}</div>
      </foreignObject>
    );
  };

  const renderBarBackground = (props: any) => {
    const { x, y, width, height, index } = props;
    if (index === 0) {
      measuredBarHeightRef.current = height;
    }
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={index % 2 === 0 ? Neutral200 : 'white'}
      />
    );
  };

  const renderBarShape = (props: any) => {
    const { className, x, y, width, height, fill } = props;
    const cellHeight = height * CELL_TO_BAR_RATIO;
    const offsetY = (height - cellHeight) / 2;

    return (
      <rect
        className={className}
        x={x}
        y={y + offsetY}
        width={width}
        height={cellHeight}
        fill={fill}
      />
    );
  };

  return (
    <ResponsiveContainer
      width='100%'
      height='100%'
      onResize={width => {
        setChartWidth(width);
      }}
      className='timeline-bar-chart'
    >
      <BarChart
        key={processedChartData.length}
        layout='vertical'
        data={processedChartData}
        margin={{ left: CHART_LEFT_MARGIN }}
        barCategoryGap={0}
        onMouseMove={state => {
          if (state.isTooltipActive) {
            setHoveredValue(state.activeLabel);
          } else {
            setHoveredValue(undefined);
          }
        }}
      >
        <XAxis
          dataKey='timeRange'
          scale='time'
          type='number'
          domain={[timeMin, timeMax]}
          tick={xAxisTick}
          ticks={xAxisTickInterval}
          interval={0}
          orientation='top'
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          formatter={timeRange => [
            `${Dfns.format('M/dd/yy')(Dfns.fromUnixTime(timeRange[0] / 1000))} - ${Dfns.format(
              'M/dd/yy'
            )(Dfns.sub({ days: 1 })(Dfns.fromUnixTime(timeRange[1] / 1000)))}`
          ]}
          labelFormatter={label => {
            const hoveredEntry = identifiedEntries.find(entry => entry.promoPeriodId === label);
            if (!hoveredEntry) {
              return undefined;
            }
            const formattedSpecialOfferName = hoveredEntry.specialOfferName ?? '';
            const formattedPercentageDiscount = hoveredEntry.percentageDiscount
              ? formatPercent(hoveredEntry.percentageDiscount / 100, 0)
              : '';
            return `${hoveredEntry.promoName} ${formattedSpecialOfferName} ${formattedPercentageDiscount}`;
          }}
        />
        <YAxis
          type='category'
          dataKey='promoPeriodId'
          tick={yAxisTick}
          tickLine={false}
          axisLine={false}
          width={Y_AXIS_TICK_WIDTH}
          interval={0}
        />
        <Bar
          dataKey='timeRange'
          fill={Brand50}
          background={renderBarBackground}
          shape={renderBarShape}
        >
          {processedChartData.map((entry, index) => (
            <Cell
              className={cn('bar-cell', {
                'hovered-value': entry.promoPeriodId === hoveredValue,
                'unhovered-value': Boolean(hoveredValue) && entry.promoPeriodId !== hoveredValue
              })}
              key={`cell-${index}`}
              fill={Brand50}
            />
          ))}
        </Bar>
        <CartesianGrid strokeWidth={1} stroke={Neutral300} horizontal={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};
